import { createServerFn } from "@tanstack/react-start";
import { eq, desc, and, ilike, or } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug, calcReadTimeMinutes, calcReadTimeFromText } from "../utils/slug.server";
import { sanitizeHtml } from "../utils/sanitize.server";
import { ensureEditorDocPersisted } from "../doc/doc-migrate.server";
import { parseEditorDocInput } from "../doc/doc-types";
import { docPlainText } from "../doc/doc-convert";
import { invalidateStoryCache } from "./public.functions";
import { coverMediaInput, resolveCoverMediaInput } from "../media/input.server";
import { parseCraftStateInput } from "../craft/parse-craft-input.server";

const blockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("p"), text: z.string() }),
  z.object({ type: z.literal("h2"), text: z.string() }),
  z.object({ type: z.literal("h3"), text: z.string() }),
  z.object({ type: z.literal("ul"), items: z.array(z.string()) }),
]);

const craftStateSchema = z.union([z.string(), z.record(z.unknown())]).optional().nullable();

const storyInput = coverMediaInput.extend({
  title: z.string().min(1),
  slug: z.string().optional(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  excerpt: z.string().optional(),
  client: z.string().optional(),
  industry: z.string().optional(),
  service: z.string().optional(),
  outcomes: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
  contentFormat: z.enum(["blocks", "html", "both"]).default("blocks"),
  contentBlocks: z.array(blockSchema).optional(),
  contentHtml: z.string().optional(),
  craftState: craftStateSchema,
  craftCustomCss: z.string().optional().nullable(),
  editorDoc: z.array(z.record(z.unknown())).optional().nullable(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
});

export const listStoriesAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ search: z.string().optional(), status: z.string().optional() }).optional())
  .handler(async ({ data }) => {
    await requirePermission("stories", "read");
    const db = getDb();
    const conditions = [];
    if (data?.status) conditions.push(eq(schema.clientStories.status, data.status as "draft" | "published" | "archived"));
    if (data?.search) {
      conditions.push(or(ilike(schema.clientStories.title, `%${data.search}%`), ilike(schema.clientStories.slug, `%${data.search}%`))!);
    }
    return db.select().from(schema.clientStories).where(conditions.length ? and(...conditions) : undefined).orderBy(desc(schema.clientStories.updatedAt));
  });

export const getStoryAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("stories", "read");
    const db = getDb();
    const [row] = await db.select().from(schema.clientStories).where(eq(schema.clientStories.id, data.id)).limit(1);
    if (!row) return null;
    return ensureEditorDocPersisted(db, schema.clientStories, row);
  });

export const createStory = createServerFn({ method: "POST" })
  .inputValidator(storyInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("stories", "write");
    const db = getDb();
    const slug = data.slug || (await generateUniqueSlug(schema.clientStories, data.title));
    const blocks = data.contentBlocks ?? [];
    const media = await resolveCoverMediaInput(data);
    const editorDoc = data.editorDoc != null ? parseEditorDocInput(data.editorDoc) : null;
    const readTimeMinutes = editorDoc?.length
      ? calcReadTimeFromText(docPlainText(editorDoc))
      : calcReadTimeMinutes(blocks);
    const [row] = await db.insert(schema.clientStories).values({
      title: data.title,
      slug,
      status: data.status,
      excerpt: data.excerpt,
      coverMediaId: media.coverMediaId,
      coverUrl: media.coverUrl,
      ogImageMediaId: media.ogImageMediaId,
      client: data.client,
      industry: data.industry,
      service: data.service,
      outcomes: data.outcomes ?? [],
      contentFormat: data.contentFormat,
      contentBlocks: blocks,
      contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : null,
      craftState: parseCraftStateInput(data.craftState),
      craftCustomCss: data.craftCustomCss ?? null,
      editorDoc,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      readTimeMinutes,
      publishedAt: data.status === "published" ? new Date() : null,
      createdBy: user.id,
      updatedBy: user.id,
    }).returning();
    await invalidateStoryCache(slug);
    return row;
  });

export const updateStory = createServerFn({ method: "POST" })
  .inputValidator(storyInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("stories", "write");
    const db = getDb();
    const [existing] = await db.select().from(schema.clientStories).where(eq(schema.clientStories.id, data.id)).limit(1);
    if (!existing) throw new Error("Not found");
    const slug = data.slug || existing.slug;
    const blocks = data.contentBlocks ?? (existing.contentBlocks as typeof data.contentBlocks) ?? [];
    const media = await resolveCoverMediaInput(data);
    const editorDoc =
      data.editorDoc !== undefined
        ? parseEditorDocInput(data.editorDoc)
        : (existing.editorDoc as typeof data.editorDoc) ?? null;
    const readTimeMinutes = editorDoc?.length
      ? calcReadTimeFromText(docPlainText(editorDoc as import("../doc/doc-types").EditorDoc))
      : calcReadTimeMinutes(blocks);
    const [row] = await db.update(schema.clientStories).set({
      title: data.title,
      slug,
      status: data.status,
      excerpt: data.excerpt,
      coverMediaId: media.coverMediaId,
      coverUrl: media.coverUrl,
      ogImageMediaId: media.ogImageMediaId,
      client: data.client,
      industry: data.industry,
      service: data.service,
      outcomes: data.outcomes ?? [],
      contentFormat: data.contentFormat,
      contentBlocks: blocks,
      contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : existing.contentHtml,
      craftState:
        data.craftState !== undefined
          ? parseCraftStateInput(data.craftState)
          : existing.craftState,
      craftCustomCss:
        data.craftCustomCss !== undefined ? data.craftCustomCss : existing.craftCustomCss,
      editorDoc,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      readTimeMinutes,
      publishedAt: data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
      updatedBy: user.id,
      updatedAt: new Date(),
    }).where(eq(schema.clientStories.id, data.id)).returning();
    await invalidateStoryCache(existing.slug);
    await invalidateStoryCache(slug);
    return row;
  });

export const deleteStory = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("stories", "delete");
    const db = getDb();
    const [existing] = await db.select().from(schema.clientStories).where(eq(schema.clientStories.id, data.id)).limit(1);
    await db.delete(schema.clientStories).where(eq(schema.clientStories.id, data.id));
    if (existing) await invalidateStoryCache(existing.slug);
    return { ok: true };
  });

export const generateStorySlug = createServerFn({ method: "POST" })
  .inputValidator(z.object({ title: z.string(), excludeId: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("stories", "write");
    return generateUniqueSlug(schema.clientStories, data.title, data.excludeId);
  });
